import React, { useCallback, useId, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";

interface ColumnDefinition<T> {
    header: string;
    valueGetter: (params: T) => string;
}

interface TaskProgressProps {
    taskId: number;
    onComplete: () => void;
}

interface BulkActionConfirmationDialogProps<T> {
    open: boolean; // Changed from 'show' to match shadcn naming
    onClose: () => void;
    onConfirm: () => Promise<number>;
    actionName: string;
    ActionIcon: React.FC<{ className?: string }>;
    actionIconClassName?: string;
    selectedRows: T[];
    columns: ColumnDefinition<T>[];
    TaskProgress: React.FC<TaskProgressProps>;
    /** Why a row cannot be actioned; such rows are flagged and left out. */
    blockedReason?: (row: T) => string | undefined;
}

const NOT_BLOCKED = "Not blocked";

function isAlreadyActioned<T>(col: ColumnDefinition<T>, value: string): boolean {
    return col.header === "Actioned" && value === "true";
}

function containsAlreadyActioned<T>(rows: T[], columns: ColumnDefinition<T>[]): boolean {
    return rows.some((row) => columns.some((col) => isAlreadyActioned(col, col.valueGetter(row))));
}

interface Blocking<T> {
    reasonOf?: (row: T) => string | undefined;
    blockedCount: number;
    distinctReasons: string[];
    nothingToAction: boolean;
}

function blockingOf<T>(rows: T[], blockedReason?: (row: T) => string | undefined): Blocking<T> {
    const reasons = rows.map((row) => blockedReason?.(row)).filter((reason): reason is string => reason !== undefined);
    return {
        reasonOf: blockedReason,
        blockedCount: reasons.length,
        distinctReasons: [...new Set(reasons)],
        nothingToAction: rows.length > 0 && reasons.length === rows.length,
    };
}

interface ConfirmationTableProps<T> {
    rows: T[];
    columns: ColumnDefinition<T>[];
    reasonOf?: (row: T) => string | undefined;
}

function ConfirmationTable<T>({ rows, columns, reasonOf }: ConfirmationTableProps<T>): React.ReactElement {
    return (
        <div className="rounded-md border overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((col, index) => (
                            <TableHead key={index}>{col.header}</TableHead>
                        ))}
                        {reasonOf && <TableHead>Blocked</TableHead>}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {columns.map((col, colIndex) => {
                                const value = col.valueGetter(row);
                                return (
                                    <TableCell key={colIndex} className={isAlreadyActioned(col, value) ? "text-red-600 bg-red-50" : ""}>
                                        {value}
                                    </TableCell>
                                );
                            })}
                            {reasonOf && <BlockedReasonCell reason={reasonOf(row)} />}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function BlockedReasonCell({ reason }: { reason: string | undefined }): React.ReactElement {
    return (
        <TableCell data-testid="bulk-action-blocked-reason" className="font-medium text-destructive">
            {reason ?? <span className="sr-only">{NOT_BLOCKED}</span>}
        </TableCell>
    );
}

function blockedMessage(blockedCount: number, totalCount: number, reasons: string[], nothingToAction: boolean): string {
    const why = reasons.join("; ");
    return nothingToAction
        ? `Nothing can be uploaded. Every selected row is blocked: ${why}`
        : `${blockedCount} of ${totalCount} selected rows will NOT be uploaded: ${why}`;
}

interface ConfirmationAlertsProps<T> {
    rows: T[];
    columns: ColumnDefinition<T>[];
    blocking: Blocking<T>;
    blockedAlertId: string;
}

function ConfirmationAlerts<T>({ rows, columns, blocking, blockedAlertId }: ConfirmationAlertsProps<T>): React.ReactElement {
    const { blockedCount, distinctReasons, nothingToAction } = blocking;
    return (
        <>
            {blockedCount > 0 && (
                <Alert variant="destructive" id={blockedAlertId} data-testid="bulk-action-blocked-alert">
                    <AlertDescription>{blockedMessage(blockedCount, rows.length, distinctReasons, nothingToAction)}</AlertDescription>
                </Alert>
            )}
            {containsAlreadyActioned(rows, columns) && (
                <Alert variant="destructive">
                    <AlertDescription>Some of the selected rows have already been actioned.</AlertDescription>
                </Alert>
            )}
            {!nothingToAction && (
                <Alert role="note" className="bg-yellow-50 border-yellow-200">
                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">You won't be able to cancel this action. Are you sure you want to proceed?</AlertDescription>
                </Alert>
            )}
        </>
    );
}

interface ConfirmationFooterProps {
    confirmed: boolean;
    isDone: boolean;
    nothingToAction: boolean;
    blockedAlertId: string;
    onCancel: () => void;
    onConfirm: () => void;
    onDone: () => void;
}

function ConfirmationFooter({ confirmed, isDone, nothingToAction, blockedAlertId, onCancel, onConfirm, onDone }: ConfirmationFooterProps): React.ReactElement {
    return (
        <DialogFooter>
            {!confirmed && (
                <>
                    <Button variant="outline" onClick={onCancel}>
                        No, Cancel
                    </Button>
                    <Button onClick={onConfirm} disabled={nothingToAction} aria-describedby={nothingToAction ? blockedAlertId : undefined}>
                        Yes, Proceed
                    </Button>
                </>
            )}
            {isDone && (
                <Button variant="volt" onClick={onDone}>
                    Done
                </Button>
            )}
        </DialogFooter>
    );
}

function focusDialogItself(event: Event): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).focus();
}

function BulkActionConfirmationDialog<T>({
    open,
    onClose,
    onConfirm,
    actionName,
    ActionIcon,
    actionIconClassName,
    selectedRows,
    columns,
    TaskProgress,
    blockedReason,
}: BulkActionConfirmationDialogProps<T>): React.ReactElement {
    const [confirmed, setConfirmed] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [taskId, setTaskId] = useState(-1);

    const handleConfirm = useCallback(async () => {
        try {
            const taskId = await onConfirm();
            setTaskId(taskId);
            setConfirmed(true);
        } catch (error) {
            console.error("Action failed:", error);
        }
    }, [onConfirm]);

    const blockedAlertId = useId();
    const rows = selectedRows || [];
    const blocking = blockingOf(rows, blockedReason);

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="flex max-h-[calc(100dvh-2rem)] flex-col sm:max-w-[min(72rem,calc(100%-2rem))]"
                data-testid="bulk-action-confirmation-dialog"
                onOpenAutoFocus={focusDialogItself}
            >
                <DialogHeader className="flex gap-6 pb-8">
                    <DialogTitle className="text-xl flex flex-row items-center font-semibold m-0 p-0 gap-2">
                        <ActionIcon className={actionIconClassName} /> Confirm {actionName}
                    </DialogTitle>
                </DialogHeader>

                <div
                    role="region"
                    aria-label={confirmed ? "Upload progress" : "Items to upload"}
                    tabIndex={0}
                    className="min-h-0 flex-1 overflow-y-auto rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {!confirmed ? (
                        <div className="space-y-4">
                            <DialogDescription className="text-base text-gray-500">
                                {blocking.nothingToAction
                                    ? "None of the following items can be uploaded to short term stay registry:"
                                    : "You're about to upload to short term stay registry the following items:"}
                            </DialogDescription>
                            <ConfirmationTable rows={rows} columns={columns} reasonOf={blocking.reasonOf} />
                            <ConfirmationAlerts rows={rows} columns={columns} blocking={blocking} blockedAlertId={blockedAlertId} />
                        </div>
                    ) : (
                        <TaskProgress
                            taskId={taskId}
                            onComplete={() => {
                                setTaskId(-1);
                                setIsDone(true);
                            }}
                        />
                    )}
                </div>

                <ConfirmationFooter
                    confirmed={confirmed}
                    isDone={isDone}
                    nothingToAction={blocking.nothingToAction}
                    blockedAlertId={blockedAlertId}
                    onCancel={() => {
                        setTaskId(-1);
                        onClose();
                    }}
                    onConfirm={handleConfirm}
                    onDone={() => {
                        onClose();
                        setIsDone(false);
                        setConfirmed(false);
                        setTaskId(-1);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

export default BulkActionConfirmationDialog;
